// src/services/api.js - UPDATED VERSION with image settings endpoints
import axios from 'axios';

// Use relative URL for Vite proxy
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:2000/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // Increased timeout
    withCredentials: true,
});

// IMPROVED: Helper function to get token with better fallback logic
function getStoredToken() {
    // Priority order: localStorage (remember me) -> sessionStorage (session only)
    const localToken = localStorage.getItem('token');
    const rememberMe = localStorage.getItem('rememberMe') === 'true';

    if (localToken && rememberMe) {
        // console.log('🔐 Using remembered token from localStorage');
        return localToken;
    }

    const sessionToken = sessionStorage.getItem('token');
    if (sessionToken) {
        // console.log('🔐 Using session token from sessionStorage');
        return sessionToken;
    }

    // Fallback: check localStorage even without rememberMe flag (for backwards compatibility)
    if (localToken) {
        // console.log('🔐 Using localStorage token (legacy mode)');
        return localToken;
    }

    console.warn('🔐 No authentication token found');
    return null;
}

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);


// Enhanced response interceptor with better error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const { response } = error;

        // Enhanced error information
        const enhancedError = {
            ...error,
            status: response?.status,
            url: error.config?.url,
            message: response?.data?.message || error.message,
            data: response?.data
        };

        // Handle Instagram automation specific errors
        if (error.config?.url?.includes('instagram-automation')) {
            enhancedError.isInstagramAutomationError = true;
            enhancedError.troubleshooting = response?.data?.troubleshooting || [];
        }

        // Handle specific error cases
        if (response?.status === 429) {
            // Rate limit exceeded
            console.warn('⚠️ Rate limit exceeded:', {
                url: error.config?.url,
                limit: response.data?.rateLimitInfo?.max,
                window: response.data?.rateLimitInfo?.windowMs,
                retryAfter: response.data?.retryAfter,
                message: response.data?.message
            });

            enhancedError.isRateLimit = true;
            enhancedError.retryAfter = response.data?.retryAfter;
            enhancedError.rateLimitInfo = response.data?.rateLimitInfo;
        } else if (response?.status === 401) {
            console.warn('🔐 Authentication error:', error.config?.url);
            enhancedError.isAuthError = true;
        } else if (response?.status >= 500) {
            console.error('🔥 Server error:', {
                status: response.status,
                url: error.config?.url,
                message: response.data?.message
            });
            enhancedError.isServerError = true;
        }

        console.error('❌ API Error:', enhancedError);
        return Promise.reject(enhancedError);
    }
);

// API endpoints
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (userData) => api.post('/auth/register', userData),
    validateToken: () => api.get('/auth/validate'),
    logout: () => api.post('/auth/logout'),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// Posts API
export const postsAPI = {
    list: () => api.get('/posts'),
    create: () => api.post('/posts/generate', {}, { timeout: 120000 }),
    createWithContent: (contentId) => api.post('/posts/generate-with-content', { contentId },  { timeout: 120000 } ),
    createWithRefresh: (maxStaleHours = 24) => api.post('/posts/generate-with-refresh', { maxStaleHours }),
    createBatch: (count, useRandomContent = true) => api.post('/posts/generate-batch', { count, useRandomContent }),
    approve: (id, variant) => api.post(`/posts/${id}/approve`, { variant }),
    publish: (id) => api.post(`/posts/${id}/publish`),
    delete: (id) => api.delete(`/posts/${id}`),
    retry: (id) => api.post(`/posts/${id}/retry`),
    updatePlatforms: (id, platforms) => api.put(`/posts/${id}/platforms`, { platforms }),
    getAvailablePlatforms: () => api.get('/posts/available-platforms'),
    getSuggestions: () => api.get('/posts/suggestions/content'),
    getById: (id) => api.get(`/posts/${id}`),
    update: (id, data) => api.put(`/posts/${id}`, data),
    getPublishingLog: (params = {}) => api.get('/posts/publishing-log', { params }),
    clone: (id) => api.post(`/posts/${id}/clone`),
    updateCaption: (id, caption) => api.put(`/posts/${id}/caption`, { caption }),
    updateHashtags: (id, hashtags) => api.put(`/posts/${id}/hashtags`, { hashtags }),
    updatePromptAndRegenerate: (id, prompt) => api.put(`/posts/${id}/regenerate`, { prompt }, { timeout: 120000 }),
};


export const contentCacheAPI = {
    getBlocked: async () => api.get('/moderation/blocked-content')
    // Add other content cache methods as needed
};

export const settingsAPI = {
    getCredentials: () => api.get('/settings/credentials'),
    saveCredentials: (credentials) => api.post('/settings/credentials', credentials),

    // NEW: Image settings
    getImageSettings: () => api.get('/settings/image-settings'),
    saveImageSettings: (settings) => api.post('/settings/image-settings', settings),

    getSetupStatus: () =>
        api.get('/settings/setup-status').then(res => res.data),
    // Email settings
    getEmailSettings: () => api.get('/settings/email-notifications'),
    saveEmailSettings: (settings) => api.post('/settings/email-notifications', settings),

    getEmailNotifications: () =>
        api.get('/settings/email-notifications').then(res => res.data),

    saveEmailNotifications: (settings) =>
        api.post('/settings/email-notifications', settings).then(res => res.data),

    testApprovalEmail: () =>
        api.post('/settings/test-approval-email').then(res => res.data),

    // Usage and quota
    getUsageStats: () => api.get('/settings/usage-stats'),
    getQuota: () => api.get('/settings/quota'),

    // Get quota information
    getQuotaInfo: () => api.get('/settings/quota'),

    // Refresh usage stats (force recalculation)
    refreshUsageStats: () => api.post('/settings/usage-stats/refresh'),

    // Get usage history (if implemented)
    getUsageHistory: (params = {}) => api.get('/settings/usage-history', { params }),

    // Platform settings
    getPlatforms: () => api.get('/settings/platforms'),
    savePlatforms: (platforms) => api.post('/settings/platforms', { platforms }),

    // Account settings
    getAccount: () => api.get('/settings/account'),
    updateAccount: (data) => api.put('/settings/account', data),

    // Content sources
    getContentSources: () => api.get('/settings/content-sources'),

    saveContentSources: (sources) => api.post('/settings/content-sources', { sources }),



    // LinkedIn setup
    getLinkedInStatus: () => api.get('/settings/linkedin/status'),
    saveLinkedInCredentials: (credentials) => api.post('/settings/linkedin/app-credentials', credentials),
    startLinkedInOAuth: () => api.post('/settings/linkedin/start-oauth'),
    testLinkedIn: () => api.post('/settings/linkedin/test'),
    validateLinkedIn: () => api.get('/settings/linkedin/validate'),
    checkLinkedInCredentials: () => api.get('/settings/linkedin/credentials-check'),

    // Twitter validation and testing
    validateTwitter: () => api.get('/settings/twitter/validate'),
    testTwitter: () => api.post('/settings/twitter/test'),

    // NEW: Instagram validation and testing
    validateInstagram: () => api.get('/settings/instagram/validate'),
    testInstagram: () => api.post('/settings/instagram/test'),

    validateFacebook: () => api.get('/settings/facebook/validate'),
    testFacebook: () => api.post('/settings/facebook/test'),

    // Combined Meta platforms setup (for both Facebook & Instagram)
    setupMetaPlatforms: (data) => api.post('/settings/meta-platforms/setup', data),

    // Optional: Get available Facebook pages for selection
    getFacebookPages: (accessToken) => api.post('/settings/facebook/pages', { accessToken }),

    // NEW: Bluesky validation and testing
    validateBluesky: () => api.get('/settings/bluesky/validate'),
    testBluesky: () => api.post('/settings/bluesky/test'),

    // NEW: Discord validation and testing
    validateDiscord: () => api.get('/settings/discord/validate'),
    testDiscord: () => api.post('/settings/discord/test'),


};

export const instagramAutomationAPI = {
    // Auto-discover Instagram Business accounts from access token
    autoDiscover: (accessToken) =>
        api.post('/instagram-automation/discover', { accessToken }),

    // Perform complete automated Instagram setup
    autoSetup: (data) =>
        api.post('/instagram-automation/auto-setup', data),

    // Get current setup progress for user
    getProgress: () =>
        api.get('/instagram-automation/progress'),

    // Validate access token and check permissions
    validateToken: (accessToken) =>
        api.post('/instagram-automation/validate-token', { accessToken }),

    // Test Instagram API connection with current credentials
    testConnection: (data = {}) =>
        api.post('/instagram-automation/test-connection', data),

    // Admin: Check health of all Instagram integrations
    healthCheck: () =>
        api.get('/instagram-automation/health-check'),

    // Admin: Automatically fix missing business IDs
    batchFix: () =>
        api.post('/instagram-automation/batch-fix'),

    // Helper: Complete setup with user-selected account
    setupWithSelectedAccount: (accessToken, selectedAccountId) =>
        api.post('/instagram-automation/auto-setup', {
            accessToken,
            selectedAccountId
        })
};



export const contentAPI = {
    getCached: (params = {}) => api.get('/content-cache', { params }),
    refresh: (sources = []) => api.post('/content-cache/refresh', { sources }),
    refreshStale: (maxAgeHours = 24) => api.post('/content-cache/refresh-stale', { maxAgeHours }),
    delete: (id) => api.delete(`/content-cache/${id}`),
    getById: (id) => api.get(`/content-cache/${id}`),
    getRandomSelect: (excludeRecent = true) => api.get('/content-cache/random/select', { params: { excludeRecent } }),
    markAsUsed: (id) => api.post(`/content-cache/${id}/use`),
    getStats: () => api.get('/content-cache/stats/overview'),
};

export const dashboardAPI = {
    getDashboardData: async () => {
        try {
            const [usage, quota, platforms, instagramProgress] = await Promise.all([
                settingsAPI.getUsageStats(),
                settingsAPI.getQuotaInfo(),
                settingsAPI.getPlatforms(),
                instagramAutomationAPI.getProgress().catch(() => ({ data: null })) // Optional, don't fail if not available
            ]);

            return {
                usage: usage.data?.data,
                quota: quota.data?.data,
                platforms: platforms.data?.data?.platforms,
                instagramProgress: instagramProgress.data
            };
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            throw error;
        }
    }
};

export const cacheAPI = {
    invalidateUsageCache: () => {
        if (window.queryClient) {
            window.queryClient.invalidateQueries('usageStats');
            window.queryClient.invalidateQueries('quotaInfo');
            window.queryClient.invalidateQueries('headerUsageStats');
            window.queryClient.invalidateQueries('instagramProgress'); // NEW
        }
    },

    invalidateInstagramCache: () => {
        if (window.queryClient) {
            window.queryClient.invalidateQueries('instagramValidation');
            window.queryClient.invalidateQueries('instagramProgress');
            window.queryClient.invalidateQueries('platformSettings');
        }
    },

    refreshAllData: async () => {
        try {
            if (window.queryClient) {
                await window.queryClient.invalidateQueries();
            }
            return await dashboardAPI.getDashboardData();
        } catch (error) {
            console.error('Failed to refresh all data:', error);
            throw error;
        }
    }
};


export const billingAPI = {
    getSubscription: () => api.get('/billing/subscription'),
    getPricing: () => api.get('/billing/pricing'),
    createCheckout: (priceId) => api.post('/billing/checkout', { priceId }),
    getSession: (sessionId) => api.get(`/billing/session/${sessionId}`),
    cancelSubscription: (data) => api.post('/billing/cancel', data),
    downgradeSubscription: (targetPlan) => api.post('/billing/downgrade', { targetPlan }),

    getUsageAndBilling: async () => {
        try {
            const [subscription, usage, quota] = await Promise.all([
                api.get('/billing/subscription'),
                api.get('/settings/usage-stats'),
                api.get('/settings/quota')
            ]);

            return {
                subscription: subscription.data,
                usage: usage.data,
                quota: quota.data
            };
        } catch (error) {
            console.error('Failed to fetch usage and billing data:', error);
            throw error;
        }
    },

    validateSubscriptionStatus: () => api.get('/billing/validate-token'),
    getUsageStats: () => api.get('/settings/usage-stats'),


    getUsage: (period = 'current') => api.get('/billing/usage', { params: { period } }),
    getQuotaInfo: () => api.get('/billing/quota'), // This should map to the middleware endpoint

    // Admin endpoints
    syncSubscription: () => api.post('/billing/sync-subscription'),
    validateToken: () => api.get('/billing/validate-token'),

    // Billing history
    getBillingHistory: (params = {}) => api.get('/billing/history', { params }),

    reactivateSubscription: () => api.post('/billing/reactivate'),

    getHistory: (params = {}) => api.get('/billing/history', { params }),
    getUpcomingInvoice: () => api.get('/billing/upcoming-invoice'),
    updatePaymentMethod: () => api.post('/billing/update-payment'),
    applyPromoCode: (promoCode) => api.post('/billing/apply-promo', { promoCode }),

    // Preview and simulation
    previewUpgrade: (targetPriceId) => api.post('/billing/preview-upgrade', { targetPriceId }),
    simulateUpgrade: (targetPriceId, dryRun = true) => api.post('/billing/simulate-upgrade', { targetPriceId, dryRun }),
};

// Admin API
export const adminAPI = {
    getCacheHealth: () => api.get('/admin/cache/health'),
    refreshCache: (userId = null) => api.post('/admin/cache/refresh', { userId }),
    performMaintenance: () => api.post('/admin/cache/maintenance'),
    resetUsage: (userId) => api.post('/admin/reset-usage', { userId }),
    getCustomers: (params = {}) => api.get('/billing/admin/customers', { params }),
    fixEntitlements: () => api.post('/billing/admin/fix-entitlements'),

    // NEW: Instagram automation admin functions
    getInstagramHealth: () => instagramAutomationAPI.healthCheck(),
    fixInstagramSetups: () => instagramAutomationAPI.batchFix(),

};

export const usageHelpers = {
    async getCurrentUsage() {
        try {
            const response = await billingAPI.getUsage();
            return response.data?.data || null;
        } catch (error) {
            console.error('Failed to get current usage:', error);
            return null;
        }
    },

    async canGeneratePost() {
        try {
            const usage = await this.getCurrentUsage();
            if (!usage) return false;
            return usage.current.remaining > 0;
        } catch (error) {
            console.error('Failed to check generation capability:', error);
            return false;
        }
    },

    async getUsagePercentage() {
        try {
            const usage = await this.getCurrentUsage();
            if (!usage) return 0;
            return usage.current.usagePercent || 0;
        } catch (error) {
            console.error('Failed to get usage percentage:', error);
            return 0;
        }
    },

    formatUsageText(usage) {
        if (!usage) return 'Usage data unavailable';

        const { current, plan } = usage;
        return `${plan} plan • ${current.postsGenerated}/${current.postsGenerated + current.remaining} posts this month`;
    }
};

export const usageAPI = {
    // Get current usage statistics
    getUsageStats: () => api.get('/settings/usage-stats'),

    // Get quota information
    getQuotaInfo: () => api.get('/settings/quota'),

    // Refresh usage stats (force recalculation)
    refreshUsageStats: () => api.post('/settings/usage-stats/refresh'),

    // Get usage history (if implemented)
    getUsageHistory: (params = {}) => api.get('/settings/usage-history', { params })
};


export const apiHelpers = {
    isQuotaExceeded(error) {
        return error.response?.status === 429 &&
            error.response?.data?.message?.includes('quota');
    },

    isAuthError(error) {
        return error.response?.status === 401;
    },

    isPermissionError(error) {
        return error.response?.status === 403;
    },

    // NEW: Instagram automation specific error checking
    isInstagramAutomationError(error) {
        return error.isInstagramAutomationError === true;
    },

    getInstagramTroubleshooting(error) {
        if (this.isInstagramAutomationError(error)) {
            return error.troubleshooting || [];
        }
        return [];
    },

    getErrorMessage(error) {
        if (this.isQuotaExceeded(error)) {
            return 'You have reached your monthly post limit. Please upgrade your plan to continue.';
        }

        if (this.isAuthError(error)) {
            return 'Your session has expired. Please log in again.';
        }

        if (this.isPermissionError(error)) {
            return 'You do not have permission to perform this action.';
        }

        // Instagram automation specific errors
        if (this.isInstagramAutomationError(error)) {
            const troubleshooting = this.getInstagramTroubleshooting(error);
            let message = error.message || 'Instagram setup failed';

            if (troubleshooting.length > 0) {
                message += '\n\nTroubleshooting steps:\n' + troubleshooting.map(step => `• ${step}`).join('\n');
            }

            return message;
        }

        return error.response?.data?.message ||
            error.message ||
            'An unexpected error occurred. Please try again.';
    }
};

// Health check
export const healthAPI = {
    check: () => api.get('/health'),
    billing: () => api.get('/billing/debug'),
    instagram: () => instagramAutomationAPI.healthCheck(),

    // Comprehensive health check including Instagram automation
    checkAll: async () => {
        try {
            const [general, billing, instagram] = await Promise.allSettled([
                api.get('/health'),
                api.get('/billing/debug'),
                instagramAutomationAPI.healthCheck()
            ]);

            return {
                general: general.status === 'fulfilled' ? general.value : { status: 'error', error: general.reason },
                billing: billing.status === 'fulfilled' ? billing.value : { status: 'error', error: billing.reason },
                instagram: instagram.status === 'fulfilled' ? instagram.value : { status: 'error', error: instagram.reason },
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            console.error('Comprehensive health check failed:', error);
            return {
                status: 'error',
                error: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
};

// Image API for testing
export const imageAPI = {
    test: () => api.get('/images/test'),
    getInfo: (filename) => api.get(`/images/info/${filename}`),
};

export const userAPI = {
    getProfile: () => api.get('/auth/validate'), // This returns user info
    updateProfile: (data) => api.put('/settings/account', data),
    getUsageStats: () => api.get('/settings/usage-stats'), // NEW endpoint needed
    refreshUser: () => api.get('/auth/validate'), // Refresh user data including usage stats

    // User preferences
    updatePreferences: (preferences) => api.put('/settings/preferences', preferences),
    getPreferences: () => api.get('/settings/preferences'),
    getInstagramSetupStatus: () => instagramAutomationAPI.getProgress(),
};

// Enhanced billing test suite
export const testBillingAPI = async () => {
    // console.group('🧪 Billing API Test Suite');

    try {
        // console.log('1. Testing subscription status...');
        const subscription = await billingAPI.getSubscription();
        // console.log('✅ Subscription status:', subscription.status);

        // console.log('2. Testing pricing configuration...');
        const pricing = await billingAPI.getPricing();
        // console.log('✅ Pricing:', pricing.status, 'configured:', pricing.data?.data?.stripeConfigured);

        // console.log('3. Testing subscription validation...');
        const validation = await billingAPI.validateSubscriptionStatus();
        // console.log('✅ Validation result:', validation);

        // console.log('4. Testing usage stats...');
        const usage = await billingAPI.getUsageStats();
        // console.log('✅ Usage stats:', usage.status);

    } catch (error) {
        console.error('❌ Billing API test failed:', error.message);
    }

    console.groupEnd();
};

// Test function for debugging
export const testAPI = async () => {
    // console.group('🧪 Complete API Test Suite');

    try {
        // console.log('1. Testing general health...');
        const health = await api.get('/health');
        // console.log('✅ Health check:', health.status);

        // console.log('2. Testing auth validation...');
        const auth = await api.get('/auth/validate');
        // console.log('✅ Auth validation:', auth.status);

        // console.log('3. Testing posts endpoint...');
        const posts = await api.get('/posts');
        // console.log('✅ Posts:', posts.status, 'data length:', posts.data?.data?.length || 'unknown');

        // console.log('4. Testing Instagram automation...');
        try {
            const instagramProgress = await instagramAutomationAPI.getProgress();
            // console.log('✅ Instagram automation:', instagramProgress.status, 'setup complete:', instagramProgress.data?.isComplete);
        } catch (error) {
            console.log('⚠️ Instagram automation not available or not set up');
        }

        // console.log('5. Testing billing...');
        try {
            const billing = await billingAPI.getSubscription();
            // console.log('✅ Billing:', billing.status);
        } catch (error) {
            console.log('⚠️ Billing not configured or accessible');
        }

    } catch (error) {
        console.error('❌ API test failed:', error.message);
    }

    console.groupEnd();
};

export const schedulingAPI = {
    // Get user's schedules
    getSchedules: () => api.get('/scheduling'),

    // Get plan limits for scheduling
    getPlanLimits: () => api.get('/scheduling/plan-limits'),

    // Create new schedule
    createSchedule: (scheduleData) => api.post('/scheduling', scheduleData),

    // Update existing schedule
    updateSchedule: (id, scheduleData) => api.put(`/scheduling/${id}`, scheduleData),

    // Pause schedule
    pauseSchedule: (id) => api.post(`/scheduling/${id}/pause`),

    // Resume schedule
    resumeSchedule: (id) => api.post(`/scheduling/${id}/resume`),

    // Trigger schedule manually
    triggerSchedule: (id) => api.post(`/scheduling/${id}/trigger`),

    // Delete schedule
    deleteSchedule: (id) => api.delete(`/scheduling/${id}`),

    // Get schedule statistics
    getScheduleStats: (id) => api.get(`/scheduling/${id}/stats`),

    // Get scheduler health (admin)
    getSchedulerHealth: () => api.get('/scheduling/health'),

    // Validate cron expression
    validateCronExpression: (cronExpression, userPlan) =>
        api.post('/scheduling/validate-cron', { cronExpression, userPlan }),

    // Get next run times for cron expression
    getNextRunTimes: (cronExpression, timezone = 'UTC', count = 5) =>
        api.post('/scheduling/next-runs', { cronExpression, timezone, count })
};

export const automationAPI = {
    autoDiscover: (data) =>
        fetch('/api/instagram-automation/discover', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(data)
        }).then(res => res.json()),

    autoSetup: (data) =>
        fetch('/api/instagram-automation/auto-setup', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(data)
        }).then(res => res.json()),

    validateToken: (data) =>
        fetch('/api/instagram-automation/validate-token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(data)
        }).then(res => res.json()),

    getProgress: () =>
        fetch('/api/instagram-automation/progress', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        }).then(res => res.json()),

    testConnection: (data) =>
        fetch('/api/instagram-automation/test-connection', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(data)
        }).then(res => res.json())
};

// Export the configured axios instance
export default api;

